import { useEffect, useState } from "react";

import { amendmentApi } from "../services";
import type { AmendmentHistory } from "../types";

export type UseAmendmentHistory = {
  history: AmendmentHistory | null;
  loading: boolean;
  error: string | null;
};

/**
 * Reads the history of one amendment. It hangs off the dialog rather than a
 * store: every screen showing an amendment can open it, and none of them shares
 * that state with the others.
 *
 * It holds no reset of its own, so mount it under a key that changes with the
 * amendment — otherwise a second opening would flash the first one's story.
 */
export function useAmendmentHistory(
  amendmentId: number | null,
): UseAmendmentHistory {
  const [state, setState] = useState<UseAmendmentHistory>({
    history: null,
    loading: null !== amendmentId,
    error: null,
  });

  useEffect(() => {
    if (null === amendmentId) {
      return;
    }

    const controller = new AbortController();

    amendmentApi
      .fetchHistory(amendmentId, { signal: controller.signal })
      .then((history) => setState({ history, loading: false, error: null }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          history: null,
          loading: false,
          error: error instanceof Error ? error.message : String(error),
        });
      });

    return () => controller.abort();
  }, [amendmentId]);

  return state;
}
